import type { DraggableProvidedDragHandleProps } from "@hello-pangea/dnd";
import { Form } from "react-bootstrap";
import { PencilFill } from "react-bootstrap-icons";
import DragIndicator from "@/images/icon-drag-indicator.svg?react";
import Close from "@/images/icon-drag-menu-close.svg?react";
import type { QuestionItem } from "@/lib/types";

interface Props {
  groupIndex: number;
  questionIndex: number;
  questionCount: number;
  checkedQuestionCount: number;
  editQuestionGroup?: number;
  editQuestionNumber?: number;
  editQuestionText?: string;
  setEditQuestionText: React.Dispatch<React.SetStateAction<string>>;
  q: QuestionItem;
  startingNumber: number;
  editActive: boolean;
  highlightDefinitions: (
    text: string,
    key: string,
  ) => string | React.ReactNode[];
  handleDeleteItem: (id: number | undefined) => void;
  handleEditingBlur: () => void;
  handleEditingQuestion: (
    original: string,
    groupIndex: number,
    questionIndex: number,
  ) => void;
  handleOnKeyDown(e: React.KeyboardEvent): void;
  handleMouseDown: (
    groupIndex: number,
    questionIndex: number,
    e: React.MouseEvent<HTMLDivElement, MouseEvent>,
  ) => void;
  handleMouseEnter: (groupIndex: number, questionIndex: number) => void;
  handleMouseUp: () => void;
  handleIndividualQuestionCheck: (
    groupIndex: number,
    questionIndex: number,
  ) => void;
  dragHandleProps: DraggableProvidedDragHandleProps | null | undefined;
}

export default function Question({
  groupIndex,
  questionIndex,
  questionCount,
  checkedQuestionCount,
  editQuestionGroup,
  editQuestionNumber,
  editQuestionText,
  setEditQuestionText,
  q,
  startingNumber,
  editActive,
  highlightDefinitions,
  handleDeleteItem,
  handleEditingBlur,
  handleEditingQuestion,
  handleIndividualQuestionCheck,
  handleOnKeyDown,
  handleMouseDown,
  handleMouseEnter,
  handleMouseUp,
  dragHandleProps,
}: Props) {
  const handleMouseDownWrapper = (
    e: React.MouseEvent<HTMLDivElement, MouseEvent>,
  ) => {
    // Check if the target is the Form.Control input
    const target = e.target as HTMLElement;
    if (target.closest("textarea") || target.closest("input")) {
      return; // Don't fire the handler if clicking on the input
    }
    handleMouseDown(groupIndex, questionIndex, e);
  };

  const handleMouseEnterWrapper = (
    e: React.MouseEvent<HTMLDivElement, MouseEvent>,
  ) => {
    // Check if the target is the Form.Control input
    const target = e.target as HTMLElement;
    if (target.closest("textarea") || target.closest("input")) {
      return; // Don't fire the handler if hovering over the input
    }
    handleMouseEnter(groupIndex, questionIndex);
  };

  const handleMouseUpWrapper = (
    e: React.MouseEvent<HTMLDivElement, MouseEvent>,
  ) => {
    // Check if the target is the Form.Control input
    const target = e.target as HTMLElement;
    if (target.closest("textarea") || target.closest("input")) {
      return; // Don't fire the handler if releasing mouse over the input
    }
    handleMouseUp();
  };

  return (
    <div
      key={`question-wrapper-${questionCount++}`}
      className={`${q.isChecked ? "selected" : ""} question-item`}
      onMouseDown={handleMouseDownWrapper}
      onMouseEnter={handleMouseEnterWrapper}
      onMouseUp={handleMouseUpWrapper}
    >
      <div
        key={`question-check-wrapper-${questionCount}`}
        className="question-item-check-wrapper"
      >
        <Form.Check
          id={`question-check-${questionCount}`}
          key={`question-check-${questionCount}`}
          type={"checkbox"}
          label=""
          checked={q.isChecked}
          onChange={() =>
            handleIndividualQuestionCheck(groupIndex, questionIndex)
          }
        />
      </div>
      {/* Data attribute is added here to let the drag/drop function know this should NOT effect checking off boxes */}
      <div {...dragHandleProps} data-drag-handle="true">
        {/* Needs to displayed via CSS this way because react dnd spits out console warnings when their drag element is removed from the DOM */}
        <DragIndicator style={{ display: q.isChecked ? "block" : "none" }} />
      </div>
      <div
        key={`question-item-number-${questionCount}`}
        className="question-item-number"
      >
        {q.isChecked && `${startingNumber + checkedQuestionCount - 1}.`}
      </div>
      <div
        key={`question-content-${questionCount}`}
        className={`question-item-content${editActive ? "nohov" : ""}`}
      >
        {editQuestionGroup === groupIndex &&
        editQuestionNumber === questionIndex ? (
          <div className="edit-input-container">
            <Form.Control
              id={`edit-${questionCount}`}
              as="textarea"
              autoFocus
              value={editQuestionText}
              style={{ height: "100px" }}
              onChange={(e) => setEditQuestionText(e.target.value)}
              onBlur={handleEditingBlur}
              onKeyDown={handleOnKeyDown}
              required
            />
          </div>
        ) : (
          <div key={`question-text-${questionCount}`} className="question-text">
            {q.isChecked ? (
              <div className="question-text-checked">
                {highlightDefinitions(
                  q.displayText ?? q.text ?? "",
                  `question-highlight-${questionCount}`,
                )}
              </div>
            ) : (
              <div className="question-text-unchecked">
                {q.displayText ?? q.text ?? ""}
              </div>
            )}
          </div>
        )}
        <div className={`question-controls ${editActive ? "nohov" : ""}`}>
          {!(
            editQuestionGroup === groupIndex &&
            editQuestionNumber === questionIndex
          ) ? (
            <PencilFill
              key={`edit-pencil-${questionCount}`}
              onClick={() =>
                handleEditingQuestion(
                  q.displayText ?? q.text ?? "",
                  groupIndex,
                  questionIndex,
                )
              }
            />
          ) : null}
          <Close onClick={() => handleDeleteItem(q.interrogatoryId)} />
        </div>
      </div>
    </div>
  );
}
